/**
 * Account profile.
 *
 * Read-only for now: the account endpoints expose no profile mutation, so this
 * shows what MindPeer knows rather than pretending to let the user edit it.
 */
import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';
import { Alert, Card, CardHeader } from '@/components/ui';
import { useAuth } from '@/hooks/use-auth';
import { formatDateTime, fullName } from '@/lib/format';

export function ProfilePage() {
  const { user } = useAuth();
  if (!user) return null;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader title="Your profile" description="Details held by your MindPeer account." />

        <dl className="divide-y divide-slate-100">
          <Row label="Name">{fullName(user)}</Row>
          <Row label="Email">
            <span className="break-all">{user.email}</span>
            {user.emailVerified ? (
              <Badge tone="green">Verified</Badge>
            ) : (
              <Badge tone="amber">Unverified</Badge>
            )}
          </Row>
          <Row label="Account status">
            <span className="capitalize">{user.status}</span>
          </Row>
          <Row label="Organization">
            {user.activeOrganization ? (
              <>
                {user.activeOrganization.name}
                <Link
                  to="/account/organizations"
                  className="text-sm font-medium text-brand-700 hover:underline"
                >
                  Change
                </Link>
              </>
            ) : (
              <span className="text-slate-500">Not assigned yet</span>
            )}
          </Row>
          {user.platformRoles.length > 0 && (
            <Row label="Platform roles">{user.platformRoles.join(', ')}</Row>
          )}
          <Row label="Member since">{formatDateTime(user.createdAt)}</Row>
        </dl>
      </Card>

      {!user.emailVerified && (
        <Alert tone="warning" title="Your email isn't verified">
          Until you verify your address you won&apos;t be able to open MindPeer modules.{' '}
          <Link to="/verify-email" className="font-medium underline">
            Send a new verification link
          </Link>
          .
        </Alert>
      )}
    </div>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 py-3 sm:grid sm:grid-cols-3">
      <dt className="text-sm font-medium text-slate-500">{label}</dt>
      <dd className="flex flex-wrap items-center gap-3 text-sm text-slate-900 sm:col-span-2">
        {children}
      </dd>
    </div>
  );
}

function Badge({ tone, children }: { tone: 'green' | 'amber'; children: ReactNode }) {
  const tones = {
    green: 'bg-green-50 text-green-700 ring-green-200',
    amber: 'bg-amber-50 text-amber-800 ring-amber-200',
  } as const;
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${tones[tone]}`}>
      {children}
    </span>
  );
}
